import { useEffect, useState } from "react";
import { Grid3x3 } from "lucide-react";
import { theme, card, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { PortalLink } from "../../components/PortalLink";
import { LoadingBlock } from "../../components/Spinner";
import { fmtDay, tableHeadRow } from "./shared";

// Readiness heatmap on the resilience overview: one row per business service with a business
// impact analysis, one cell per readiness check, all read from what GET /business-impact already
// derives (backend/src/services/businessImpact.ts: tier, tolerance issues, readiness gaps).
// Every cell opens that analysis on the Business impact tab.

interface ReadinessGap { kind: string; subject: string; message: string; severity: "high" | "medium" | "low" }
interface Analysis {
  id: string;
  displayId: number | null;
  name: string;
  status: string;
  rtoHours: number | null;
  derived: {
    tier: 1 | 2 | 3 | 4;
    toleranceIssues: Array<{ field: string; message: string }>;
    readiness: { score: number; checks: number; passed: number; gaps: ReadinessGap[] };
    lastExercisedAt: string | null;
    dependencies: Array<{ id: string }>;
  };
  suppliers: Array<{ supplierId: string }>;
}

type Cell = "pass" | "fail" | "na";

interface Column { id: string; label: string; help: string; value: (a: Analysis) => { state: Cell; title: string } }

const gapsOf = (a: Analysis, ...kinds: string[]) => a.derived.readiness.gaps.filter((g) => kinds.includes(g.kind));
const cellFromGaps = (a: Analysis, applies: boolean, kinds: string[], ok: string): { state: Cell; title: string } => {
  if (!applies) return { state: "na", title: "Not applicable" };
  const g = gapsOf(a, ...kinds);
  return g.length ? { state: "fail", title: g.map((x) => x.message).join(" ") } : { state: "pass", title: ok };
};

const COLUMNS: Column[] = [
  {
    id: "rto", label: "RTO fits", help: "A recovery time objective is set and fits inside the maximum tolerable period",
    value: (a) => {
      const issues = a.derived.toleranceIssues.filter((t) => t.field === "rtoHours" || t.field === "mtpdHours");
      return a.rtoHours == null ? { state: "fail", title: "No recovery time objective set." } : issues.length ? { state: "fail", title: issues.map((i) => i.message).join(" ") } : { state: "pass", title: "The recovery time objective fits the tolerable period." };
    },
  },
  {
    id: "recover", label: "Recovers in time", help: "Every asset it rests on, through the dependency graph, recovers within the RTO",
    value: (a) => a.derived.dependencies.length === 0 && a.suppliers.length === 0
      ? { state: "fail", title: "No assets or suppliers are named, so recovery cannot be checked." }
      : cellFromGaps(a, a.rtoHours != null && a.derived.dependencies.length > 0, ["asset_rto_exceeds", "asset_rto_unknown", "asset_retired"], "Every dependency recovers within the RTO."),
  },
  { id: "plans", label: "Contingency plans", help: "Each directly named asset has a contingency plan", value: (a) => cellFromGaps(a, a.derived.dependencies.length > 0, ["asset_no_plan"], "Every named asset has a contingency plan.") },
  { id: "tested", label: "Tested", help: "Exercised in the last twelve months", value: (a) => cellFromGaps(a, true, ["asset_untested", "bia_untested"], a.derived.lastExercisedAt ? `Last exercised ${fmtDay(a.derived.lastExercisedAt)}.` : "Tested.") },
  { id: "suppliers", label: "Supplier agreements", help: "Every supplier it rests on has an agreement in force", value: (a) => cellFromGaps(a, a.suppliers.length > 0, ["supplier_no_agreement"], "Every supplier has an agreement in force.") },
];

const CELL_STYLE: Record<Cell, { bg: string; color: string; text: string }> = {
  pass: { bg: theme.color.successBg, color: theme.color.successText, text: "Yes" },
  fail: { bg: theme.color.dangerBg,  color: theme.color.dangerText,  text: "No" },
  na:   { bg: theme.color.panelAlt,  color: theme.color.faint,       text: "n/a" },
};

const scoreStyle = (score: number) => score >= 80 ? CELL_STYLE.pass : score >= 50 ? { bg: theme.color.warningBg, color: theme.color.warningText, text: "" } : CELL_STYLE.fail;
const ref = (a: Analysis) => (a.displayId != null ? `BIA-${String(a.displayId).padStart(3, "0")}` : "BIA");

export function OverviewReadiness() {
  const [analyses, setAnalyses] = useState<Analysis[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.get<{ analyses: Analysis[] }>("/business-impact")
      .then((d) => setAnalyses(d.analyses.filter((a) => a.status !== "archived").sort((x, y) => x.derived.tier - y.derived.tier || x.name.localeCompare(y.name))))
      .catch((e) => { setError(apiErrorMessage(e, "Could not load the business impact analyses")); setAnalyses([]); });
  }, []);

  return (
    <div style={{ ...card, padding: 0 }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Grid3x3 size={15} style={{ color: theme.color.heading }} />
        <span style={{ ...text.sectionTitle, margin: 0 }}>Service readiness</span>
        <span style={text.hint}>can each business service be recovered within its objectives?</span>
      </div>
      {error && <div style={{ ...text.body, padding: "10px 18px", color: theme.color.dangerText }}>{error}</div>}
      {!analyses ? <div style={{ padding: 18 }}><LoadingBlock /></div> : analyses.length === 0 ? (
        <div style={{ padding: 18, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ ...text.body, color: theme.color.muted, maxWidth: 620 }}>No business impact analysis yet. Analyse the services the organisation cannot do without: how long each can be down, how fast it must recover and what it rests on. Readiness then shows here for each one.</div>
          <PortalLink to="/resilience/bia" style={{ ...btnPrimary, textDecoration: "none" }}>Start a business impact analysis</PortalLink>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.hint.fontSize }}>
            <thead><tr style={tableHeadRow}>
              <th style={{ padding: "9px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>Business service</th>
              <th style={{ padding: "9px 10px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>Tier</th>
              {COLUMNS.map((c) => <th key={c.id} title={c.help} style={{ padding: "9px 10px", borderBottom: "1px solid var(--c-line)", fontWeight: 600, textAlign: "center" }}>{c.label}</th>)}
              <th style={{ padding: "9px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600, textAlign: "center" }}>Readiness</th>
            </tr></thead>
            <tbody>
              {analyses.map((a) => {
                const href = `/resilience/bia?bia=${a.id}`;
                const s = scoreStyle(a.derived.readiness.score);
                return (
                  <tr key={a.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                    <td style={{ padding: "8px 14px" }}>
                      <PortalLink to={href} style={{ color: theme.color.text, fontWeight: 600, textDecoration: "none" }}>{a.name}</PortalLink>
                      <div style={text.meta}><span style={{ fontVariantNumeric: text.ref.fontVariantNumeric }}>{ref(a)}</span>{a.status === "draft" ? " · draft" : ""}</div>
                    </td>
                    <td style={{ padding: "8px 10px", color: a.derived.tier <= 2 ? theme.color.dangerText : theme.color.muted, fontWeight: 600, whiteSpace: "nowrap" }}>Tier {a.derived.tier}</td>
                    {COLUMNS.map((c) => {
                      const v = c.value(a);
                      const st = CELL_STYLE[v.state];
                      return (
                        <td key={c.id} style={{ padding: 3 }}>
                          <PortalLink to={href} title={`${c.label}: ${v.title}`} style={{ display: "block", textAlign: "center", padding: "7px 6px", borderRadius: 3, background: st.bg, color: st.color, fontWeight: 600, textDecoration: "none" }}>{st.text}</PortalLink>
                        </td>
                      );
                    })}
                    <td style={{ padding: 3 }}>
                      <PortalLink to={href} title={`${a.derived.readiness.passed} of ${a.derived.readiness.checks} readiness checks pass`} style={{ display: "block", textAlign: "center", padding: "7px 10px", borderRadius: 3, background: s.bg, color: s.color, fontWeight: 700, textDecoration: "none" }}>{a.derived.readiness.score}%</PortalLink>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
